import axios from 'axios';
import envConfig from '../env-config/';
import getPkce from 'oauth-pkce';
import jwt_decode from 'jwt-decode';
import { cacheKeys, clearCache, clearCacheAndReload, localCache, popCacheItem, sessionCache } from '../app-cache'; // prettier-ignore
import { Logger } from '@aws-amplify/core';
import { sleep } from '../';

/*******************************************************************************
 * auth-manager.js is a collection of functions to assist with
 * authentication and storage/retrieval of local user info and access tokens.
 * The application uses the OAuth 2.0 Authorization Code with PKCE Flow
 ******************************************************************************/

const authConfig = envConfig.auth;
const log = new Logger('auth-manager');

/*******************************************************************************
 * Async wrapper for getPkce
 * @returns {Promise<{verifier: string, challenge: string}>}
 ******************************************************************************/
const getPkceAsync: any = (length = 43) =>
  new Promise((resolve, reject) => {
    getPkce(length, (error, { verifier, challenge }) => {
      if (error) reject(error);
      resolve({ verifier, challenge });
    });
  });

/*******************************************************************************
 * Gets the external login url
 ******************************************************************************/
const getExternalLoginUrl = async () => {
  log.debug('getExternalLoginUrl...');

  const pkce = await getPkceAsync();
  localCache.setItem(cacheKeys.code_verifier, pkce.verifier);

  const state = await getPkceAsync();
  localCache.setItem(cacheKeys.auth_state, state.verifier);

  return (
    `https://${authConfig.domain}/oauth2/authorize?` +
    `identity_provider=${authConfig.idpName}&` +
    `redirect_uri=${authConfig.redirectUri}&` +
    `response_type=${authConfig.responseType}&` +
    `client_id=${authConfig.clientId}&` +
    `scope=${authConfig.scope}&` +
    `code_challenge_method=S256&` +
    `code_challenge=${pkce.challenge}&` +
    `state=${state.verifier}`
  );
};

/*******************************************************************************
 * Gets the external logout url
 ******************************************************************************/
const getExternalLogoutUrl = () => {
  log.debug('getExternalLogoutUrl...');
  return (
    `https://${authConfig.domain}/logout?` +
    `client_id=${authConfig.clientId}&` +
    `logout_uri=${authConfig.logoutUri}`
  );
};

/*******************************************************************************
 * Gets the refresh token
 * @returns {string}
 ******************************************************************************/
const getRefreshToken = () => {
  log.debug('getRefreshToken...');
  return localCache.getItem(cacheKeys.refresh_token);
};

/*******************************************************************************
 * Gets the refresh token request body
 * @param {string} refreshToken The refresh token
 ******************************************************************************/
const getRefreshTokenRequestBody = (refreshToken: any) => {
  log.debug('getRefreshTokenRequestBody...');
  return (
    `grant_type=refresh_token&` +
    `client_id=${authConfig.clientId}&` +
    `refresh_token=${refreshToken}`
  );
};

/*******************************************************************************
 * Gets the (jwt) token
 * @returns {string}
 ******************************************************************************/
const getToken = () => {
  log.debug('getToken...');
  return localCache.getItem(cacheKeys.token);
};

/*******************************************************************************
 * Gets the oauth2/token request url
 ******************************************************************************/
const getTokenRequestUrl = () => {
  log.debug('getTokenRequestUrl...');
  return `https://${authConfig.domain}/oauth2/token`;
};

/*******************************************************************************
 * Gets the token request body
 * @param {string} authCode The authorization code
 * @param {string} codeVerifier The code verifier for pkce
 ******************************************************************************/
const getTokenRequestBody = (authCode: any, codeVerifier: any) => {
  log.debug('getTokenRequestBody...');
  return (
    `grant_type=authorization_code&` +
    `client_id=${authConfig.clientId}&` +
    `code=${authCode}&` +
    `redirect_uri=${authConfig.redirectUri}&` +
    `code_verifier=${codeVerifier}`
  );
};

/*******************************************************************************
 * Gets the object representing the authenticted user
 * @returns {import('../typedefs').User}
 ******************************************************************************/
const getUser = () => {
  log.debug('getUser...');
  return localCache.getItem(cacheKeys.user);
};

/*******************************************************************************
 * Handles the token response data
 * @param {object} [tokenResponse]
 * @param {string} [tokenResponse.access_token]
 * @param {number} [tokenResponse.expires_in]
 * @param {string} [tokenResponse.id_token]
 * @param {string} [tokenResponse.refresh_token]
 * @param {string} [tokenResponse.token_type]
 ******************************************************************************/
const handleTokenResponse = (tokenResponse: any) => {
  const { id_token, refresh_token } = tokenResponse;

  const jwt: any = jwt_decode(id_token);
  const tokenExp = { expires: jwt.exp * 1000 };
  const user = {
    email: jwt.email,
    employeeId: jwt['custom:employee_id'],
    firstName: jwt.given_name,
    lastName: jwt.family_name,
  };

  localCache.setItem(cacheKeys.user, user);
  localCache.setItem(cacheKeys.token, id_token, tokenExp);

  if (refresh_token) {
    const now = new Date();
    const refreshExp = { expires: now.setDate(now.getDate() + 29) };
    localCache.setItem(cacheKeys.refresh_token, refresh_token, refreshExp);
  }
};

/*******************************************************************************
 * Exchanges the auth code for tokens
 * @param {string} authCode The authorization code
 ******************************************************************************/
const exchangeCodeForTokens = async (authCode: any) => {
  log.debug('exchangeCodeForTokens...');

  const codeVerifier = popCacheItem(localCache, cacheKeys.code_verifier);
  const request = {
    url: getTokenRequestUrl(),
    data: getTokenRequestBody(authCode, codeVerifier),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    method: 'post',
  };

  try {
    const response = await axios(request);
    handleTokenResponse(response.data);
  } catch (err) {
    log.error('exchangeCodeForTokens', err);
    clearCache();
    throw err;
  }
};

/*******************************************************************************
 * Launches a hidden iframe to get a new session
 ******************************************************************************/
const tokenRefresher = async () => {
  const i = document.createElement('iframe');
  i.id = 'sso_refresh_frame';
  i.title = 'sso refresh frame';
  i.style = 'display: none; visibility: hidden';
  i.height = 0;
  i.width = 0;
  i.src = window.location.origin;

  document.body.append(i);
  console.log(`Refreshing session...`, 'refresh-session', 'default', 5000);

  for (let i = 0; i < 10; ++i) {
    log.debug('trying to refresh - ', i + 1, 'sec');

    if (getToken()) {
      break;
    }

    await sleep(1000);
  }

  document.body.removeChild(i);
};

/*******************************************************************************
 * Gets the authorization token for API calls
 * @returns {Promise<{token: string}>}
 ******************************************************************************/
const getTokenForApiCall = async () => {
  log.debug('getTokenForApiCall...');

  let idToken = getToken();

  if (!idToken) {
    const refreshToken = getRefreshToken();

    const request = {
      url: getTokenRequestUrl(),
      data: getRefreshTokenRequestBody(refreshToken),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      method: 'post',
    };

    try {
      if (refreshToken) {
        const response = await axios(request);
        handleTokenResponse(response.data);
        idToken = response.data.id_token;
      } else {
        await tokenRefresher();
        idToken = getToken();
      }
    } catch (err) {
      log.error('getTokenForApiCall', err);
      clearCacheAndReload();
    }
  }

  return { token: idToken };
};

/*******************************************************************************
 * Returns a value indicating whether the user is authenticated
 ******************************************************************************/
const isAuthenticated = () => {
  log.debug('isAuthenticated...');
  return !!getToken() && !!getUser();
};

/*******************************************************************************
 * Returns the code and state query string values from the url
 ******************************************************************************/
const getAuthResponseFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return { code: urlParams.get('code'), state: urlParams.get('state') };
};

/*******************************************************************************
 * Handles the login process
 * @returns {Promise<boolean>} A value indicating whether the user is authenticated
 ******************************************************************************/
const handleLogin = async () => {
  log.debug('handleLogin...');
  const { code, state } = getAuthResponseFromUrl();
  const storedState = popCacheItem(localCache, cacheKeys.auth_state);
  try {
    if (code && storedState === state) {
      exchangeCodeForTokens(code).then(res => {
        window.location = popCacheItem(sessionCache, 'redirectAfterAuth');
      });
    } else if (!isAuthenticated()) {
      sessionCache.setItem('redirectAfterAuth', window.location.href.toString());
      window.location = await getExternalLoginUrl();
    }
  } catch (err) {
    log.error('handleLogin', err);
    throw err;
  }

  return isAuthenticated();
};

/*******************************************************************************
 * Clears the cache and navigates to the logout url
 ******************************************************************************/
const logout = () => {
  log.debug('logout...');
  clearCache();
  window.location = getExternalLogoutUrl();
};

/*******************************************************************************
 * exported api definition
 ******************************************************************************/
const authManager = {
  getAuthResponseFromUrl,
  getToken,
  getTokenForApiCall,
  getUser,
  handleLogin,
  isAuthenticated,
  logout,
};

export default authManager;
